import { CalendarClock, CheckCircle2, Circle, Flame } from "lucide-react";
import type { ReactNode } from "react";
import type { StreakStats } from "../types";

/** The hero: current streak, prominent, with today's status and longest. */
export function StreakHero({ streak }: { streak: StreakStats }) {
	return (
		<div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface-1 p-6 sm:flex-row sm:items-center sm:justify-between">
			<div className="flex items-center gap-4">
				<span className="flex size-16 flex-none items-center justify-center rounded-2xl bg-primary/10 text-primary">
					<Flame className="h-8 w-8" />
				</span>
				<div className="flex flex-col">
					<span className="eyebrow text-ink-subtle">Current streak</span>
					<span className="display-lg tabular-nums leading-none">
						{streak.current}
						<span className="ml-1.5 text-lg font-normal text-ink-subtle">
							day{streak.current === 1 ? "" : "s"}
						</span>
					</span>
					<span className="caption mt-1 text-ink-subtle">
						Longest: {streak.longest} · {streak.totalActiveDays} active days total
					</span>
				</div>
			</div>
			<div
				className={`flex items-center gap-2 self-start rounded-lg border px-3 py-2 text-sm font-medium sm:self-center ${
					streak.todayCounts
						? "border-semantic-success/30 bg-semantic-success/10 text-semantic-success"
						: "border-hairline bg-surface-2 text-ink-muted"
				}`}
			>
				{streak.todayCounts ? (
					<>
						<CheckCircle2 className="h-4 w-4" /> Today counts
					</>
				) : (
					<>
						<Circle className="h-4 w-4" /> Today not logged yet
					</>
				)}
			</div>
		</div>
	);
}

/** A compact labeled metric tile. */
export function StatTile({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
	return (
		<div className="flex min-h-[96px] flex-col gap-1.5 rounded-xl border border-hairline bg-surface-1 p-4">
			<span className="eyebrow text-ink-subtle">{label}</span>
			<span className="display-sm tabular-nums">{value}</span>
			{sub ? <span className="caption text-ink-subtle">{sub}</span> : null}
		</div>
	);
}

/** Weekly active-days goal as a segmented bar. */
export function WeeklyGoalBar({ active, target }: { active: number; target: number }) {
	const cells = Array.from({ length: target }, (_, i) => i < active);
	return (
		<div className="flex flex-col gap-2 rounded-xl border border-hairline bg-surface-1 p-4">
			<div className="flex items-center justify-between">
				<span className="eyebrow text-ink-subtle">This week</span>
				<span className="caption tabular-nums text-ink-subtle">
					{active}/{target} active days
				</span>
			</div>
			<div className="flex gap-1.5">
				{cells.map((filled, i) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length goal cells
						key={i}
						className={`h-2.5 flex-1 rounded-full ${filled ? "bg-primary" : "bg-surface-3"}`}
					/>
				))}
			</div>
			{active >= target ? (
				<span className="caption text-semantic-success">Weekly goal met 🎉</span>
			) : null}
		</div>
	);
}

/** Countdown to the next goal race. */
export function RaceCountdownCard({ race }: { race: { name: string; daysUntil: number } | null }) {
	return (
		<div className="flex min-h-[96px] flex-col gap-1.5 rounded-xl border border-hairline bg-surface-1 p-4">
			<span className="eyebrow flex items-center gap-1.5 text-ink-subtle">
				<CalendarClock className="h-3.5 w-3.5" /> Next race
			</span>
			{race ? (
				<>
					<span className="display-sm tabular-nums">
						{race.daysUntil}
						<span className="ml-1.5 text-base font-normal text-ink-subtle">
							day{race.daysUntil === 1 ? "" : "s"}
						</span>
					</span>
					<span className="caption truncate text-ink-subtle">{race.name}</span>
				</>
			) : (
				<span className="caption text-ink-subtle">No upcoming races. Add one in Goals.</span>
			)}
		</div>
	);
}
